import {
  Tag,
  Hash,
  CalendarDays,
  Package,
  IndianRupee,
  Percent,
  XCircle,
  CheckCircle2,
} from "lucide-react";

import Modal from "./Modal";
import ProductImg from "./ProductImg";
import DetailRow from "./DetailRow";
import { fmt } from "./fmt";

export default function ProductDetailsModal({
  open,
  onClose,
  selected,
  tab,
  mrpLabel,
  marginLabel,
  rangeLabel,
  requestAssign,
  requestUnassign,
}) {


  const splitBySymbol = (text, regex) => {
  if (!text || typeof text !== "string") return [];
  return text
    .split(regex)
    .map((t) => t.trim())
    .filter(Boolean);
};

  const parseFeatures = (features) => {
    if (!features || typeof features !== "string") return [];

    return features
      .split("✔")
      .map((f) => f.trim())
      .filter(Boolean)
      .map((f) => `✔ ${f}`);
  };


  if (!open || !selected) return null;

  const p = selected.product || {};

  return (
    <Modal open={open} title="Product Details" onClose={onClose} max="max-w-4xl">
      <div className="space-y-4">
        {/* Header */}
        <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4">
          <div className="flex items-center gap-3">
            <ProductImg src={p.image} name={p.name} />
            <div>
              <p className="text-xs font-semibold text-slate-500">Product</p>
              <p className="text-base font-semibold text-slate-900">{fmt(p.name)}</p>
              <p className="mt-1 text-xs font-semibold text-slate-600">
                PID: <span className="font-semibold">{fmt(selected.product_id)}</span>
                {tab === "assigned" && (
                  <>
                    {" "}
                    • CPID:{" "}
                    <span className="font-semibold">
                      {fmt(selected.company_product_id)}
                    </span>
                  </>
                )}
              </p>

              <p className="mt-1 text-sm font-semibold text-slate-900">
                Category: {(p.categories || []).map(c => c?.name).filter(Boolean).join(", ") || "—"}
              </p>
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <DetailRow icon={Tag} label="Zoho ID" value={p.zoho_id} />
          <DetailRow icon={Hash} label="HSN Code" value={p.hsn_code} />
          <DetailRow icon={CalendarDays} label="Validity" value={`${fmt(p.validity)} days`} />
          <DetailRow icon={Package} label="Claims" value={p.claims} />
          <DetailRow icon={IndianRupee} label="MRP" value={mrpLabel(p)} />
          <DetailRow
            icon={Percent}
            label="Admin Margin"
            value={tab === "assigned" ? marginLabel(p, selected.margin) : "—"}
          />

          <div className="md:col-span-1 rounded-2xl border border-blue-100 bg-white p-4">
            <p className="text-xs font-semibold text-slate-500">Valid Price Range</p>
            <p className="mt-1 text-sm font-semibold text-slate-900">
              {rangeLabel(p)}
            </p>
          </div>

          <div className="md:col-span-1 rounded-2xl border border-blue-100 bg-white p-4">
            <p className="text-xs font-semibold text-slate-500">Company Profit</p>
            <p className="mt-1 text-sm font-semibold text-slate-900">
              {p.margin}
            </p>
          </div>

          {/* Features */}
          <div className="md:col-span-4 rounded-2xl border border-emerald-100 bg-emerald-50/40 p-5">
            <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-emerald-700">
              ✔ Features
            </p>

            <div className="flex flex-wrap gap-2">
              {splitBySymbol(p.features, /✔/).map((item, i) => (
                <span
                  key={i}
                  className="rounded-full border border-emerald-300 bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800"
                >
                  ✔ {item}
                </span>
              ))}
            </div>
          </div>

          {/* Coverage */}
          <div className="md:col-span-4 rounded-2xl border border-blue-100 bg-blue-50/40 p-5">
            <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-blue-700">
              🛡 Coverage
            </p>

            <div className="flex flex-wrap gap-2">
              {splitBySymbol(p.coverage, /\./).map((item, i) => (
                <span
                  key={i}
                  className="rounded-full border border-blue-300 bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-800"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>

          {/* Exclusions */}
          <div className="md:col-span-4 rounded-2xl border border-rose-100 bg-rose-50/40 p-5">
            <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-rose-700">
              ⚠ Exclusions
            </p>

            <div className="flex flex-wrap gap-2">
              {splitBySymbol(p.exclusions, /🚫/).map((item, i) => (
                <span
                  key={i}
                  className="rounded-full border border-rose-300 bg-rose-100 px-3 py-1 text-xs font-semibold text-rose-800"
                >
                  🚫 {item}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 md:flex-row md:justify-end">
          {tab === "assigned" ? (
            <button
              onClick={() => requestUnassign(selected)}
              className="inline-flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-100"
            >
              <XCircle className="h-4 w-4" />
              Unassign
            </button>
          ) : (
            <button
              onClick={() => requestAssign(selected)}
              className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100"
            >
              <CheckCircle2 className="h-4 w-4" />
              Assign
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
